import { memo, useMemo } from 'react';

import { raiseEvent } from '@cast/analytics';

import type { ChatHistoryItem, ChatMessage } from '@cast/types';
import { ErrorBubble } from './ErrorBubble';

import { getFeedbackAnalyticsFields } from './feedbackAnalytics';
import { MessageBubble } from './MessageBubble';
import { ResponseFeedback, useResponseFeedback } from './ResponseFeedback';
import { ThinkingBubble } from './ThinkingBubble';
import { ChatTrackingProvider } from './useChatTracking';
import { Box, Stack } from '@cast/design-system';

type ConversationContext = {
  organizationId: string;
  chatId?: string;
  getChat: (id: string) => ChatHistoryItem | undefined;
  /** Live A2A contextId getter — preferred join key for post-answer events. */
  getLiveContextId?: () => string | undefined;
};

type MessagesListProps = {
  messages: ChatMessage[];
  loadingResponse: boolean;
  error: string | null;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  dummyRef: React.RefObject<HTMLDivElement | null>;
  onScrollToBottom?: () => void;
  conversationContext?: ConversationContext;
  pendingRetry?: boolean;
  onRetry?: () => void;
  onFollowUpClick?: (command: string) => void;
  /** Streamed reasoning text from a working-state status update. */
  thinkingText?: string;
};

const MessagesListComponent = ({
  messages,
  loadingResponse,
  error,
  onScroll,
  dummyRef,
  onScrollToBottom,
  conversationContext,
  pendingRetry,
  onRetry,
  onFollowUpClick,
  thinkingText,
}: MessagesListProps) => {
  const { getMessageFeedback, saveMessageFeedback, clearMessageFeedback } =
    useResponseFeedback();

  const savedContextId = conversationContext?.chatId
    ? conversationContext.getChat(conversationContext.chatId)?.contextId
    : undefined;

  // Memoized so the context reference is stable between renders — prevents
  // every MessageBubble from re-rendering when an unrelated parent state
  // (e.g. loading flag) changes without touching the join keys.
  const trackingValue = useMemo(
    () => ({
      context_id: conversationContext?.getLiveContextId?.() ?? savedContextId,
      organization_id: conversationContext?.organizationId,
    }),
    // conversationContext is already memoized in AIAssistantSidebar;
    // savedContextId changes only when the active chat changes.
    [conversationContext, savedContextId]
  );

  return (
    <ChatTrackingProvider value={trackingValue}>
      <Box
        onScroll={onScroll}
        sx={{ flex: 1, overflow: 'auto' }}
        data-testid="chat-messages-list"
      >
        <Stack
          direction="column"
          gap="16px"
          sx={{ p: 16, position: 'relative' }}
        >
          {messages.map((message, index) => {
            const isLatest = index === messages.length - 1;

            return (
              <Stack key={message.id} data-testid="chat-message" mb={8} gap={8}>
                <MessageBubble
                  message={message}
                  isLatest={isLatest}
                  pendingRetry={
                    pendingRetry && isLatest && message.role === 'agent'
                  }
                  onRetry={onRetry}
                  onFollowUpClick={onFollowUpClick}
                />
                {message.role === 'agent' && (
                  <ResponseFeedback
                    isLatest={isLatest}
                    savedFeedback={getMessageFeedback(message.id)}
                    messageText={message.text}
                    onScrollToBottom={onScrollToBottom}
                    onFeedback={(feedback) => {
                      saveMessageFeedback({
                        messageId: message.id,
                        feedback,
                      });

                      if (conversationContext) {
                        const chatItem = conversationContext.chatId
                          ? conversationContext.getChat(
                              conversationContext.chatId
                            )
                          : undefined;

                        const { reason, details } =
                          getFeedbackAnalyticsFields(feedback);

                        raiseEvent({
                          title: 'AI Feedback Submitted',
                          type: 'feedback',
                          organizationId: conversationContext.organizationId,
                          chatId: conversationContext.chatId,
                          chatTitle: chatItem?.title,
                          chatCreatedAt: chatItem?.createdAt,
                          chatUpdatedAt: chatItem?.updatedAt,
                          // Prefer the live contextId getter; fall back to
                          // the saved snapshot for historical chats.
                          context_id:
                            conversationContext.getLiveContextId?.() ??
                            chatItem?.contextId,
                          chatMessageCount: messages.length,
                          chatMessages: JSON.stringify(
                            messages.map(({ id, role }) => ({ id, role }))
                          ),
                          referrerUrl: window.location.href,
                          timestamp: new Date().toISOString(),
                          // Use the user message that triggered this agent
                          // response, not the agent message itself, so
                          // feedback is attributed to the originating query.
                          messageId:
                            messages
                              .slice(0, index)
                              .reverse()
                              .find((m) => m.role === 'user')?.id ?? message.id,
                          variant: feedback.variant,
                          reason,
                          details,
                        });
                      }
                    }}
                    onClearFeedback={() => {
                      clearMessageFeedback({ messageId: message.id });
                    }}
                  />
                )}
              </Stack>
            );
          })}

          {loadingResponse && <ThinkingBubble text={thinkingText} />}

          {error && <ErrorBubble error={error} />}
        </Stack>

        {/* dummy element used for targeted scrolling to bottom of chat*/}
        <Box ref={dummyRef} />
      </Box>
    </ChatTrackingProvider>
  );
};

export const MessagesList = memo(MessagesListComponent);
